import { useEffect, useState } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { shouldFlagRoundNotOpen } from 'src/components/BirdieRoundCard/roundNotOpen'
import { BET_CONFIG } from 'src/config'
import { useBetStore } from 'src/store/bet'
import type { MarbleGame } from 'src/types/domain/Game'
import { isBirdieGameType } from 'src/utils/gameTypeUtils'

export interface RoundNotOpenState {
  /** True from the first qualifying reject until the round changes. */
  flagged: boolean
  /** The rejection reason that raised the flag (today always `RoundClosed`). */
  reason?: string
}

/**
 * A88 — the first `RoundClosed` reject for THIS round with more than 2 s left
 * on the local closing countdown means Go never opened the round: the board is
 * priced and the clock is running, but every chip will fail. Latch it for the
 * round and log `birdie_round_not_open` once (unsampled).
 *
 * The notice itself is NOT rendered here. The BirdieBoard (Task B9 of the
 * arc-board plan) renders it in its persistent notice slot, in place of the
 * arc tiles; the shared ChipPlacementPanel is deliberately left untouched.
 */
export function useRoundNotOpen(game: MarbleGame): RoundNotOpenState {
  const [flaggedRound, setFlaggedRound] = useState<{ roundId: string; reason: string } | undefined>(
    undefined
  )
  const lastRejection = useBetStore((s) => s.lastRejection)

  const isBirdieGame = isBirdieGameType(game.track.gameType)
  const race = game.races[0]
  const roundId = race?.uuid

  useEffect(() => {
    if (!isBirdieGame || !lastRejection || !race) return
    if (flaggedRound?.roundId === race.uuid) return

    const starting = race.raceEvents.find((e) => e.eventType === 'race_starting')
    const flag = shouldFlagRoundNotOpen({
      reason: lastRejection.reason,
      rejectionRaceId: lastRejection.raceId,
      currentRaceId: race.uuid,
      closingStartedAt: starting ? Date.parse(starting.eventTimestamp) : undefined,
      closingWindowS: game.bettingClosingWindow || BET_CONFIG.DEFAULT_BETTING_CLOSING_WINDOW_SEC,
      now: Date.now()
    })
    if (!flag) return

    setFlaggedRound({ roundId: race.uuid, reason: lastRejection.reason })
    datadogLogs.logger.warn('birdie_round_not_open', {
      user_event: {
        roundId: race.uuid,
        betId: lastRejection.betId,
        reason: lastRejection.reason,
        rejectedAt: lastRejection.at
      }
    })
  }, [isBirdieGame, lastRejection, race, game.bettingClosingWindow, flaggedRound])

  // The latch is per-round: a stale flag must never bleed into the next round,
  // and the state is dropped rather than cleared so a re-entry into the same
  // round id cannot re-fire the log.
  const active = isBirdieGame && flaggedRound !== undefined && flaggedRound.roundId === roundId
  return active ? { flagged: true, reason: flaggedRound.reason } : { flagged: false }
}
